import React, { useState } from 'react';
import { FiSend, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { Header } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import ButtonLoading from '../../components/ButtonLoading';
import FundSelector from '../../components/Finance/FundSelector';

const FundTransfer = () => {
  useKYCStatusRedirect('/finance/transfers');
  const { user, school } = useStateContext();
  const schoolId = school?._id;
  const isOwner = user?.role === 0;
  const multiFundEnabled = !!school?.settings?.finance?.multi_fund_enabled;

  const [fromFundId, setFromFundId] = useState('');
  const [toFundId, setToFundId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!fromFundId || !toFundId || !amount) {
      setError('Please fill all the required fields.');
      return;
    }
    if (fromFundId === toFundId) {
      setError('Source and destination funds must be different.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      await userRequest.post(`/schools/${schoolId}/funds/transfers`, {
        from_fund_id: fromFundId,
        to_fund_id: toFundId,
        amount: Number(amount),
        ...(description && { description }),
        ...(reference && { reference }),
      });
      setSuccess(true);
      setAmount('');
      setDescription('');
      setReference('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <Header
        category="Finance"
        title="Fund Transfer"
        description="Transfer money internally between your school's named fund wallets with complete audit tracking."
      />

      <div className="max-w-lg mt-6">
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
            <FiAlertCircle className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm mb-4">
            <FiCheckCircle className="mt-0.5 shrink-0" />
            <span><b>Success!</b> Funds transferred.</span>
          </div>
        )}
        {!isOwner && (
          <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl px-4 py-3 text-sm mb-4">
            <FiAlertCircle className="mt-0.5 shrink-0" />
            <span>Only the school owner can transfer funds.</span>
          </div>
        )}

        <form onSubmit={submitHandler} className="space-y-4">
          <FundSelector schoolId={schoolId} value={fromFundId} onChange={setFromFundId} excludeFundId={toFundId} label="From Fund" />
          <FundSelector schoolId={schoolId} value={toFundId} onChange={setToFundId} excludeFundId={fromFundId} label="To Fund" />

          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">Amount (₦)</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 10000"
              className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Reallocate to secondary fund"
              className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">Reference (optional)</label>
            <input
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="Idempotency reference"
              className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          {loading ? (
            <ButtonLoading styles="w-full text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
          ) : (
            <button
              type="submit"
              disabled={!isOwner}
              className="w-full inline-flex items-center justify-center gap-2 text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-full text-sm px-5 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSend className="w-4 h-4" />
              Transfer Funds
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default FundTransfer;
